// Form values and normalizers of the Marketing forms (pure). Each normalizer
// returns the database payload or the i18n key of the first error.
import type { Identifier } from "ra-core";

import {
  amountInput,
  isInvalidAmount,
  optionalText,
  optionalUrl,
  parseAmount,
  parseOptionalDay,
  type FormResult,
} from "@/components/competitors/forms";
import { isPlatform } from "@/components/competitors/platforms";
import type { SocialPlatform } from "@/components/competitors/types";
import { weekStartOf } from "@/components/market-intel/dates";

import { fromLocalInput, toLocalInput } from "./calendar";
import {
  CHANNEL_STATUSES,
  CONTENT_FORMATS,
  CONTENT_STATUSES,
  CONTENT_VERTICALS,
  PLAN_STATUSES,
} from "./constants";
import {
  METRIC_FIELDS,
  type ChannelStatus,
  type ContentFormat,
  type ContentItem,
  type ContentMetric,
  type ContentPlan,
  type ContentPlanChannel,
  type ContentPlanPhase,
  type ContentPlanPillar,
  type ContentStatus,
  type ContentVertical,
  type MarketingChannel,
  type MetricField,
  type PlanStatus,
} from "./types";

const idOrNull = (value: string): Identifier | null =>
  value === "" ? null : /^\d+$/.test(value) ? Number(value) : value;

const idInput = (value: Identifier | null | undefined): string =>
  value == null ? "" : String(value);

// ---------- pieces ----------

export interface PieceFormValues {
  title: string;
  channel_id: string;
  format: ContentFormat;
  status: ContentStatus;
  vertical: ContentVertical;
  plan_id: string;
  phase_id: string;
  week_number: string;
  pillar_id: string;
  secondary_pillar_id: string;
  assignee_id: string;
  /** datetime-local value. */
  scheduled_at: string;
  brief: string;
  published_url: string;
}

export const pieceFormDefaults = (
  piece?: ContentItem | null,
  prefill: Partial<PieceFormValues> = {},
): PieceFormValues => ({
  title: piece?.title ?? "",
  channel_id: idInput(piece?.channel_id),
  format: piece?.format ?? "post",
  status: piece?.status ?? "idea",
  vertical: piece?.vertical ?? "general",
  plan_id: idInput(piece?.plan_id),
  phase_id: idInput(piece?.phase_id),
  week_number: piece?.week_number == null ? "" : String(piece.week_number),
  pillar_id: idInput(piece?.pillar_id),
  secondary_pillar_id: idInput(piece?.secondary_pillar_id),
  assignee_id: idInput(piece?.assignee_id),
  scheduled_at: toLocalInput(piece?.scheduled_at),
  brief: piece?.brief ?? "",
  published_url: piece?.published_url ?? "",
  ...prefill,
});

export type PiecePayload = Pick<
  ContentItem,
  | "title"
  | "channel_id"
  | "format"
  | "status"
  | "vertical"
  | "plan_id"
  | "phase_id"
  | "week_number"
  | "pillar_id"
  | "secondary_pillar_id"
  | "assignee_id"
  | "scheduled_at"
  | "brief"
  | "published_url"
>;

export const normalizePieceForm = (
  values: PieceFormValues,
): FormResult<PiecePayload> => {
  const title = values.title.trim();
  if (!title) return { ok: false, error: "marketing.form.title_required" };
  const week = values.week_number.trim();
  const weekNumber = week ? Number(week) : null;
  if (
    weekNumber != null &&
    (!Number.isInteger(weekNumber) || weekNumber < 1 || weekNumber > 53)
  )
    return { ok: false, error: "marketing.form.week_invalid" };
  const scheduled = values.scheduled_at.trim();
  const scheduledAt = scheduled ? fromLocalInput(scheduled) : null;
  if (scheduled && !scheduledAt)
    return { ok: false, error: "marketing.form.date_invalid" };
  const plan = idOrNull(values.plan_id);
  const pillar = plan == null ? null : idOrNull(values.pillar_id);
  let secondary = plan == null ? null : idOrNull(values.secondary_pillar_id);
  if (secondary != null && String(secondary) === String(pillar))
    secondary = null;
  return {
    ok: true,
    data: {
      title,
      channel_id: idOrNull(values.channel_id),
      format: CONTENT_FORMATS.includes(values.format) ? values.format : "post",
      status: CONTENT_STATUSES.includes(values.status) ? values.status : "idea",
      vertical: CONTENT_VERTICALS.includes(values.vertical)
        ? values.vertical
        : "general",
      plan_id: plan,
      phase_id: plan == null ? null : idOrNull(values.phase_id),
      week_number: weekNumber,
      pillar_id: pillar,
      secondary_pillar_id: secondary,
      assignee_id: idOrNull(values.assignee_id),
      scheduled_at: scheduledAt,
      brief: optionalText(values.brief),
      published_url: optionalUrl(values.published_url),
    },
  };
};

// ---------- channels ----------

export interface ChannelFormValues {
  platform: SocialPlatform;
  name: string;
  handle: string;
  url: string;
  status: ChannelStatus;
  objective: string;
  monthly_budget: string;
  notes: string;
}

export const channelFormDefaults = (
  channel: MarketingChannel | null | undefined,
  locale: string,
): ChannelFormValues => ({
  platform: channel?.platform ?? "instagram",
  name: channel?.name ?? "",
  handle: channel?.handle ?? "",
  url: channel?.url ?? "",
  status: channel?.status ?? "activo",
  objective: channel?.objective ?? "",
  monthly_budget: amountInput(channel?.monthly_budget, locale),
  notes: channel?.notes ?? "",
});

export type ChannelPayload = Pick<
  MarketingChannel,
  | "platform"
  | "name"
  | "handle"
  | "url"
  | "status"
  | "objective"
  | "monthly_budget"
  | "notes"
>;

export const normalizeChannelForm = (
  values: ChannelFormValues,
): FormResult<ChannelPayload> => {
  const name = values.name.trim();
  if (!name) return { ok: false, error: "marketing.form.name_required" };
  const budget = parseAmount(values.monthly_budget);
  if (isInvalidAmount(budget))
    return { ok: false, error: "marketing.form.amount_invalid" };
  return {
    ok: true,
    data: {
      platform: isPlatform(values.platform) ? values.platform : "otro",
      name,
      handle: optionalText(values.handle),
      url: optionalUrl(values.url),
      status: CHANNEL_STATUSES.includes(values.status)
        ? values.status
        : "activo",
      objective: optionalText(values.objective),
      monthly_budget: budget,
      notes: optionalText(values.notes),
    },
  };
};

// ---------- plans ----------

export interface PillarRow {
  id?: Identifier;
  code: string;
  name: string;
  description: string;
  color: string;
}

export interface PhaseRow {
  id?: Identifier;
  number: string;
  name: string;
  start_week: string;
  end_week: string;
  cadence: string;
}

export interface CadenceRow {
  id?: Identifier;
  channel_id: string;
  posts_per_week: string;
}

export interface PlanFormValues {
  name: string;
  objective: string;
  start_date: string;
  end_date: string;
  status: PlanStatus;
  notes: string;
  pillars: PillarRow[];
  phases: PhaseRow[];
  cadences: CadenceRow[];
}

export const planFormDefaults = (
  plan: ContentPlan | null | undefined,
  children: {
    pillars: readonly ContentPlanPillar[];
    phases: readonly ContentPlanPhase[];
    cadences: readonly ContentPlanChannel[];
  },
  locale: string,
): PlanFormValues => ({
  name: plan?.name ?? "",
  objective: plan?.objective ?? "",
  start_date: plan?.start_date ?? "",
  end_date: plan?.end_date ?? "",
  status: plan?.status ?? "activo",
  notes: plan?.notes ?? "",
  pillars: children.pillars.map((pillar) => ({
    id: pillar.id,
    code: pillar.code,
    name: pillar.name,
    description: pillar.description ?? "",
    color: pillar.color ?? "",
  })),
  phases: children.phases.map((phase) => ({
    id: phase.id,
    number: String(phase.number),
    name: phase.name ?? "",
    start_week: phase.start_week == null ? "" : String(phase.start_week),
    end_week: phase.end_week == null ? "" : String(phase.end_week),
    cadence: phase.cadence ?? "",
  })),
  cadences: children.cadences.map((cadence) => ({
    id: cadence.id,
    channel_id: String(cadence.channel_id),
    posts_per_week: amountInput(cadence.posts_per_week, locale),
  })),
});

type Child<T> = Omit<T, "plan_id" | "created_at" | "updated_at"> & {
  id?: Identifier;
};

export interface PlanPayload {
  plan: Pick<
    ContentPlan,
    "name" | "objective" | "start_date" | "end_date" | "status" | "notes"
  >;
  pillars: Child<ContentPlanPillar>[];
  phases: Child<ContentPlanPhase>[];
  cadences: Child<ContentPlanChannel>[];
}

const optionalWeek = (value: string): number | null | typeof NaN => {
  const raw = value.trim();
  if (!raw) return null;
  const week = Number(raw);
  return Number.isInteger(week) && week >= 1 ? week : Number.NaN;
};

export const normalizePlanForm = (
  values: PlanFormValues,
): FormResult<PlanPayload> => {
  const name = values.name.trim();
  if (!name) return { ok: false, error: "marketing.form.name_required" };
  const start = parseOptionalDay(values.start_date);
  const end = parseOptionalDay(values.end_date);
  if (!start.ok || !end.ok)
    return { ok: false, error: "marketing.form.date_invalid" };
  if (start.day && end.day && end.day < start.day)
    return { ok: false, error: "marketing.form.period_invalid" };

  const pillars = values.pillars
    .filter((row) => row.code.trim() || row.name.trim())
    .map((row, index) => ({
      id: row.id,
      code: row.code.trim().toUpperCase(),
      name: row.name.trim(),
      description: optionalText(row.description),
      color: optionalText(row.color),
      position: index + 1,
    }));
  if (pillars.some((pillar) => !pillar.code || !pillar.name))
    return { ok: false, error: "marketing.form.pillar_incomplete" };
  if (new Set(pillars.map((pillar) => pillar.code)).size !== pillars.length)
    return { ok: false, error: "marketing.form.pillar_duplicate" };

  const phases = values.phases
    .filter((row) => row.number.trim() || row.name.trim())
    .map((row) => ({
      id: row.id,
      number: Number(row.number.trim()),
      name: optionalText(row.name),
      start_week: optionalWeek(row.start_week),
      end_week: optionalWeek(row.end_week),
      description: null,
      cadence: optionalText(row.cadence),
    }));
  if (
    phases.some(
      (phase) =>
        !Number.isInteger(phase.number) ||
        phase.number < 1 ||
        Number.isNaN(phase.start_week) ||
        Number.isNaN(phase.end_week) ||
        (phase.start_week != null &&
          phase.end_week != null &&
          phase.end_week < phase.start_week),
    )
  )
    return { ok: false, error: "marketing.form.phase_invalid" };
  if (new Set(phases.map((phase) => phase.number)).size !== phases.length)
    return { ok: false, error: "marketing.form.phase_duplicate" };

  const cadences = values.cadences
    .filter((row) => row.channel_id)
    .map((row) => ({
      id: row.id,
      channel_id: idOrNull(row.channel_id)!,
      posts_per_week: parseAmount(row.posts_per_week),
      notes: null,
    }));
  if (
    cadences.some(
      (row) =>
        row.posts_per_week == null ||
        isInvalidAmount(row.posts_per_week) ||
        row.posts_per_week <= 0,
    )
  )
    return { ok: false, error: "marketing.form.cadence_invalid" };
  if (
    new Set(cadences.map((row) => String(row.channel_id))).size !==
    cadences.length
  )
    return { ok: false, error: "marketing.form.cadence_duplicate" };

  return {
    ok: true,
    data: {
      plan: {
        name,
        objective: optionalText(values.objective),
        start_date: start.day,
        end_date: end.day,
        status: PLAN_STATUSES.includes(values.status)
          ? values.status
          : "activo",
        notes: optionalText(values.notes),
      },
      pillars,
      phases: phases as Child<ContentPlanPhase>[],
      cadences: cadences as Child<ContentPlanChannel>[],
    },
  };
};

// ---------- weekly metrics ----------

export type MetricsTarget =
  { kind: "piece"; id: Identifier } | { kind: "channel"; id: Identifier };

export type MetricsFormValues = Record<MetricField, string> & {
  week_start: string;
  notes: string;
};

export const metricsFormDefaults = (
  today: string,
  row: ContentMetric | null | undefined,
  locale: string,
): MetricsFormValues =>
  ({
    week_start: row?.week_start ?? weekStartOf(today),
    notes: row?.notes ?? "",
    ...Object.fromEntries(
      METRIC_FIELDS.map((field) => [
        field,
        field === "spend"
          ? amountInput(row?.spend, locale)
          : row?.[field] == null
            ? ""
            : String(row[field]),
      ]),
    ),
  }) as MetricsFormValues;

export type MetricsPayload = Pick<
  ContentMetric,
  "content_item_id" | "channel_id" | "week_start" | "notes" | "source"
> &
  Record<MetricField, number | null>;

/**
 * Weekly metrics of a piece or a channel: any day picked is moved to the
 * Monday of its week (the database only accepts Mondays).
 */
export const normalizeMetricsForm = (
  values: MetricsFormValues,
  target: MetricsTarget,
): FormResult<MetricsPayload> => {
  const day = parseOptionalDay(values.week_start);
  if (!day.ok || !day.day)
    return { ok: false, error: "marketing.form.week_start_required" };
  const numbers = {} as Record<MetricField, number | null>;
  for (const field of METRIC_FIELDS) {
    const value = parseAmount(values[field], field !== "spend");
    if (isInvalidAmount(value))
      return { ok: false, error: "marketing.form.amount_invalid" };
    numbers[field] = value;
  }
  if (METRIC_FIELDS.every((field) => numbers[field] == null))
    return { ok: false, error: "marketing.form.metrics_empty" };
  return {
    ok: true,
    data: {
      content_item_id: target.kind === "piece" ? target.id : null,
      channel_id: target.kind === "channel" ? target.id : null,
      week_start: weekStartOf(day.day),
      notes: optionalText(values.notes),
      source: "manual",
      ...numbers,
    },
  };
};
